import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { telecharger } from "../api";
import Layout from "../components/Layout";
import Journal from "../components/Journal";
import ImportVehicules from "../components/ImportVehicules";
import ComptesClient from "../components/ComptesClient";
import ComptesEditeur from "../components/ComptesEditeur";
import IdentiteClient from "../components/IdentiteClient";
import SupprimerClient from "../components/SupprimerClient";
import ModulesClient from "../components/ModulesClient";
import UsageClient, { depuisQuand, joursDepuis } from "../components/UsageClient";
import { SqueletteTableau } from "../components/Squelette";
import Notification from "../components/Notification";

const CLIENT_VIDE = {
  nom: "",
  slug: "",
  couleur_primaire: "#000000",
  couleur_secondaire: "#FFFFFF",
  couleur_accent: "#E30613",
  ville: "",
  contact: "",
};

const STATUTS = [
  { valeur: "essai", label: "Essai" },
  { valeur: "actif", label: "Actif" },
  { valeur: "suspendu", label: "Suspendu" },
];

/** Identifiant technique deduit du nom : « Transports Durand » -> transports-durand. */
function slugifier(nom) {
  return (nom || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function messageErreur(err, defaut) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return defaut;
}

export default function Plateforme({ user, onLogout, onConsulter }) {
  const [clients, setClients] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const [enCours, setEnCours] = useState(false);

  const [creationOuverte, setCreationOuverte] = useState(false);
  const [nouveau, setNouveau] = useState(CLIENT_VIDE);

  const [modalComptes, setModalComptes] = useState(null);  // client cible
  const [modalModules, setModalModules] = useState(null);  // client cible
  const [modalIdentite, setModalIdentite] = useState(null);  // client cible
  const [modalSuppression, setModalSuppression] = useState(null);
  const [modalUsage, setModalUsage] = useState(null);      // client cible
  const [modalEditeurs, setModalEditeurs] = useState(false); // nos comptes

  // Le super-admin n'a pas d'ecran « Utilisateurs » -- il n'a pas de flotte,
  // et cet ecran est celui d'un client. Sans le bouton ci-dessous, il n'avait
  // AUCUN moyen de changer son propre mot de passe depuis l'application : la
  // route existait, l'ecran manquait. Un compte qui ouvre les donnees de tous
  // les clients est justement celui dont le mot de passe doit pouvoir tourner.
  const [modalJournal, setModalJournal] = useState(null);
  const [modalImport, setModalImport] = useState(null);
  const [exportEnCours, setExportEnCours] = useState(null);

  const charger = useCallback(() => {
    setChargement(true);
    api
      .get("/plateforme/clients")
      .then(({ data }) => setClients(data))
      .catch((err) => setErreur(messageErreur(err, "Impossible de charger les clients.")))
      .finally(() => setChargement(false));
  }, []);

  useEffect(charger, [charger]);

  // Un compte client qui taperait l'URL n'a rien a faire ici. Ce n'est PAS une
  // protection : le serveur repond 404 sur /plateforme/*. C'est du confort.
  if (user.role !== "superadmin") return <Navigate to="/" replace />;

  const annoncer = (texte) => {
    setMessage(texte);
    setErreur("");
    setTimeout(() => setMessage(""), 8000);
  };

  const creer = (e) => {
    e.preventDefault();
    setEnCours(true);
    setErreur("");
    api
      .post("/plateforme/clients", nouveau)
      .then(({ data }) => {
        setNouveau(CLIENT_VIDE);
        setCreationOuverte(false);
        annoncer(
          `Client « ${data.nom} » créé, en période d’essai. ` +
            "Créez maintenant son premier administrateur."
        );
        charger();
      })
      .catch((err) => setErreur(messageErreur(err, "Création impossible.")))
      .finally(() => setEnCours(false));
  };

  const changerStatut = (client, statut) => {
    setErreur("");
    api
      .patch(`/plateforme/clients/${client.id}`, { statut_abonnement: statut })
      .then(() => {
        annoncer(
          statut === "suspendu"
            ? `${client.nom} est suspendu : ses salariés n’ont plus accès à l’application.`
            : `${client.nom} est de nouveau ${statut}.`
        );
        charger();
      })
      .catch((err) => setErreur(messageErreur(err, "Modification impossible.")));
  };


  const exporter = (client) => {
    setExportEnCours(client.id);
    setErreur("");
    telecharger(
      `/plateforme/clients/${client.id}/export`,
      `export_${client.slug}.zip`
    )
      .then((nom) =>
        annoncer(
          `${nom} téléchargé. Il contient des données personnelles : ` +
            "à transmettre par un canal sûr et à détruire ensuite."
        )
      )
      .catch((err) => setErreur(messageErreur(err, "Export impossible.")))
      .finally(() => setExportEnCours(null));
  };

  const consulter = (client) => {
    setErreur("");
    api
      .post(`/plateforme/consulter/${client.id}`)
      .then(({ data }) => {
        // Le jeton de consultation remplace le jeton courant : toutes les
        // requetes suivantes portent sur ce client, en lecture seule.
        onConsulter(data.access_token, data.client);
        // Rechargement COMPLET plutot qu'une navigation interne : la marque
        // (nom, logo, couleurs) est chargee au demarrage de l'application. Une
        // simple navigation laissait la barre afficher « Plateforme » alors
        // que les couleurs, elles, avaient bascule -- un etat a moitie change
        // est plus deroutant qu'une seconde d'attente.
        window.location.assign("/");
      })
      .catch((err) => setErreur(messageErreur(err, "Consultation impossible.")));
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="entete-page">
        <h1>Clients</h1>
        <div className="actions-groupe">
          {/* Meme raison que cote client : « Mon compte » est le seul endroit
              ou l'on change son propre mot de passe. */}
          <Link className="btn" to="/mon-compte">
            Mon compte
          </Link>
          {/* Nos propres accès, pas ceux d'un client. Placé ici, à côté de
              « Mon compte », parce que c'est le même sujet : qui entre chez
              nous. Les accès d'un CLIENT se gèrent depuis sa ligne. */}
          <button className="btn" onClick={() => setModalEditeurs(true)}>
            Comptes éditeur
          </button>
          <button
            className="btn-accent"
            onClick={() => {
              setCreationOuverte((v) => !v);
              setErreur("");
            }}
          >
            {creationOuverte ? "Annuler" : "+ Nouveau client"}
          </button>
        </div>
      </div>

      <Notification message={message} />
      {erreur && <p className="erreur">{erreur}</p>}

      {creationOuverte && (
        <form className="formulaire" onSubmit={creer}>
          <div className="grille-form">
            <label className="champ">
              <span className="champ-label">Nom de l’entreprise *</span>
              <input
                required
                value={nouveau.nom}
                onChange={(e) =>
                  setNouveau({
                    ...nouveau,
                    nom: e.target.value,
                    slug: slugifier(e.target.value),
                  })
                }
              />
            </label>
            <label className="champ">
              <span className="champ-label">Identifiant *</span>
              <input
                required
                value={nouveau.slug}
                onChange={(e) => setNouveau({ ...nouveau, slug: slugifier(e.target.value) })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Ville</span>
              <input
                value={nouveau.ville}
                onChange={(e) => setNouveau({ ...nouveau, ville: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Contact</span>
              <input
                value={nouveau.contact}
                onChange={(e) => setNouveau({ ...nouveau, contact: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Couleur principale</span>
              <input
                type="color"
                value={nouveau.couleur_primaire}
                onChange={(e) => setNouveau({ ...nouveau, couleur_primaire: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Couleur d’accent</span>
              <input
                type="color"
                value={nouveau.couleur_accent}
                onChange={(e) => setNouveau({ ...nouveau, couleur_accent: e.target.value })}
              />
            </label>
          </div>
          <p className="sous-info">
            Le client démarre en <strong>période d’essai</strong> : l’application
            fonctionne normalement. Vous la basculerez en « actif » à la signature.
          </p>
          <div className="actions-form">
            <button className="btn-accent" type="submit" disabled={enCours}>
              {enCours ? "Création…" : "Créer le client"}
            </button>
          </div>
        </form>
      )}

      {chargement && (
        <table className="tableau">
          <thead>
            <tr>
              <th>Client</th>
              <th>Parc</th>
              <th>Comptes</th>
              <th>Modules</th>
              <th>Activité</th>
              <th>Abonnement</th>
              <th>Actions</th>
            </tr>
          </thead>
          <SqueletteTableau lignes={4} colonnes={7} />
        </table>
      )}

      {!chargement && clients && clients.length === 0 && (
        <p className="vide">
          Aucun client pour le moment. Créez-en un pour commencer.
        </p>
      )}

      {!chargement && clients && clients.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <th>Client</th>
              <th>Parc</th>
              <th>Comptes</th>
              <th>Modules</th>
              <th>Activité</th>
              <th>Abonnement</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id}>
                <td>
                  <strong>{c.nom}</strong>
                  {c.ville && <span className="sous-info"> · {c.ville}</span>}
                  <br />
                  <span className="sous-info">{c.contact || "—"}</span>
                </td>
                <td>{c.nb_vehicules} véhicule{c.nb_vehicules > 1 ? "s" : ""}</td>
                <td>{c.nb_utilisateurs}</td>
                <td>
                  {[
                    c.module_vgp && "VGP",
                    c.module_operations && "Coûts",
                    c.module_stock && "Stock",
                    c.module_financement && "Financement",
                    c.module_location && "Location",
                  ]
                    .filter(Boolean)
                    .join(" · ") || <span className="note-discrete">Parc seul</span>}
                </td>
                {/* CLIQUABLE, et non un bouton de plus : la cellule d'actions
                    en porte déjà huit, et le neuvième faisait déborder la page
                    (mesuré le 17/08). Le délai est aussi l'information qu'on
                    vient chercher — autant qu'il soit la porte d'entrée. */}
                <td>
                  <button
                    className={
                      "lien-action"
                      + (joursDepuis(c.derniere_activite) === null
                         || joursDepuis(c.derniere_activite) >= 14
                          ? " lien-danger" : "")
                    }
                    onClick={() => setModalUsage(c)}
                    title="Voir qui se sert du logiciel chez ce client"
                  >
                    {depuisQuand(c.derniere_activite)}
                  </button>
                </td>
                <td>
                  <select
                    value={c.statut_abonnement}
                    onChange={(e) => changerStatut(c, e.target.value)}
                  >
                    {STATUTS.map((s) => (
                      <option key={s.valeur} value={s.valeur}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="actions-cellule">
                  <button className="lien-action" onClick={() => consulter(c)}>
                    Consulter
                  </button>
                  <button className="lien-action" onClick={() => setModalImport(c)}>
                    Importer un parc
                  </button>
                  <button className="lien-action" onClick={() => setModalJournal(c)}>
                    Journal
                  </button>
                  <button
                    className="lien-action"
                    disabled={exportEnCours === c.id}
                    onClick={() => exporter(c)}
                  >
                    {exportEnCours === c.id ? "Export…" : "Exporter"}
                  </button>
                  <button className="lien-action" onClick={() => setModalComptes(c)}>
                    Comptes
                  </button>
                  <button className="lien-action" onClick={() => setModalModules(c)}>
                    Modules
                  </button>
                  <button className="lien-action" onClick={() => setModalIdentite(c)}>
                    {c.logo_televerse ? "Identité ✓" : "Identité"}
                  </button>
                  <button
                    className="lien-action lien-danger"
                    onClick={() => setModalSuppression(c)}
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="sous-info" style={{ marginTop: 24 }}>
        « Consulter » ouvre les données d’un client <strong>en lecture seule</strong> :
        le serveur refuse toute modification tant que vous y êtes. Un client
        suspendu perd immédiatement l’accès, y compris ses sessions ouvertes.
      </p>

      {modalImport && (
        <ImportVehicules
          url={`/plateforme/clients/${modalImport.id}/import`}
          titre={`Importer le parc — ${modalImport.nom}`}
          sousTitre={
            "Seule écriture que vous fassiez dans les données d’un client. " +
            "Elle n’ajoute que des véhicules, et laisse une trace dans son journal."
          }
          onFini={charger}
          onFermer={() => setModalImport(null)}
        />
      )}

      {modalJournal && (
        <Journal
          url={`/plateforme/clients/${modalJournal.id}/journal`}
          titre={`Activité — ${modalJournal.nom}`}
          sousTitre="Exactement ce que ce client voit de son côté."
          onFermer={() => setModalJournal(null)}
        />
      )}


      {modalComptes && (
        <ComptesClient
          client={modalComptes}
          onFermer={() => setModalComptes(null)}
          onChange={charger}
        />
      )}

      {modalModules && (
        <ModulesClient
          client={modalModules}
          onFermer={() => setModalModules(null)}
          // La liste porte les modules : sans ce rafraichissement, rouvrir la
          // modale montrerait l'etat d'avant la bascule.
          onChange={charger}
        />
      )}

      {modalIdentite && (
        <IdentiteClient
          client={modalIdentite}
          onFermer={() => setModalIdentite(null)}
          // La liste porte l'indicateur « Identité ✓ » et les couleurs : sans
          // ce rafraichissement, elle mentirait jusqu'au prochain chargement.
          onChange={charger}
        />
      )}

      {modalSuppression && (
        <SupprimerClient
          client={modalSuppression}
          onFermer={() => setModalSuppression(null)}
          // Le client vient de disparaitre : sans rechargement, sa ligne
          // resterait affichee et le prochain clic tomberait sur un 404.
          onChange={charger}
        />
      )}

      {modalUsage && (
        <UsageClient client={modalUsage} onFermer={() => setModalUsage(null)} />
      )}

      {modalEditeurs && (
        <ComptesEditeur user={user} onFermer={() => setModalEditeurs(false)} />
      )}
    </Layout>
  );
}
